import { useNavigate, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, Pencil, Trash2 } from 'lucide-react';
import { BackButton } from '@/components/common/BackButton';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/common/ErrorState';
import { Can } from '@/components/common/Can';
import { RBAC_PERMISSIONS } from '@/constants/permissions';
import { PageHeader } from '@/components/layout/PageHeader';
import { userDetailQueryOptions } from '../api/users.api';
import { useUserActionModals } from '../hooks/useUserActionModals';
import {
  OrganizationSection,
  PersonalSection,
  ProfileLayout,
  UserTimestamps,
} from '../components/profile';
import { UserAccessSection } from '@/features/roles/components/UserAccessSection';
import { ResetPasswordModal } from '../components/ResetPasswordModal';
import { DeleteUserModal } from '../components/DeleteUserModal';

const HEADER_BUTTON_CLASS = 'shrink-0 gap-2 max-sm:size-10 max-sm:px-0';

interface UserViewPageProps {
  userId: number;
  fallbackPath?: string | undefined;
}

export function UserViewPage({ userId, fallbackPath = '/users' }: UserViewPageProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const {
    data: user,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(userDetailQueryOptions(String(userId)));

  const {
    userToResetPassword,
    userToDelete,
    openResetPasswordModal,
    openDeleteModal,
    closeResetPasswordModal,
    closeDeleteModal,
  } = useUserActionModals();

  function handleBack() {
    if (router.history.canGoBack()) {
      router.history.back();
    } else {
      void navigate({ to: fallbackPath });
    }
  }

  function handleEdit() {
    void navigate({ to: '/users/edit', search: { userId, from: fallbackPath } });
  }

  function handleResetPassword() {
    if (user) openResetPasswordModal(user);
  }

  function handleDelete() {
    if (user) openDeleteModal(user);
  }

  function handleDeleted() {
    void navigate({ to: fallbackPath, replace: true });
  }

  const backButton = <BackButton onClick={handleBack} />;

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load this user"
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        action={backButton}
        fill
      />
    );
  }

  if (!isLoading && !user) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <p className="text-sm font-medium text-muted-foreground">User not found</p>
        {backButton}
      </div>
    );
  }

  return (
    <main className="flex flex-col gap-6">
      <PageHeader
        title="User Profile"
        leading={backButton}
        actions={
          <div className="flex items-center gap-2">
            <Can code={RBAC_PERMISSIONS.USER_EDIT}>
              <Button variant="outline" onClick={handleEdit} className={HEADER_BUTTON_CLASS}>
                <Pencil className="size-4" aria-hidden />
                <span className="max-sm:sr-only">Edit</span>
              </Button>
            </Can>
            <Can code={RBAC_PERMISSIONS.USER_CHANGE_PASSWORD}>
              <Button
                variant="outline"
                onClick={handleResetPassword}
                disabled={!user}
                className={HEADER_BUTTON_CLASS}
              >
                <KeyRound className="size-4" aria-hidden />
                <span className="max-sm:sr-only">Reset Password</span>
              </Button>
            </Can>
            <Can code={RBAC_PERMISSIONS.USER_DELETE}>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={!user}
                className={HEADER_BUTTON_CLASS}
              >
                <Trash2 className="size-4" aria-hidden />
                <span className="max-sm:sr-only">Delete</span>
              </Button>
            </Can>
          </div>
        }
      />

      <ProfileLayout user={user}>
        <OrganizationSection user={user} />
        <PersonalSection user={user} />
        <UserAccessSection userId={userId} />
        <UserTimestamps
          createdAt={user?.createdAt}
          updatedAt={user?.updatedAt}
          isLoading={isLoading}
        />
      </ProfileLayout>

      <ResetPasswordModal
        user={userToResetPassword}
        open={!!userToResetPassword}
        onOpenChange={(open) => !open && closeResetPasswordModal()}
      />
      <DeleteUserModal
        user={userToDelete}
        open={!!userToDelete}
        onOpenChange={(open) => !open && closeDeleteModal()}
        onDeleted={handleDeleted}
      />
    </main>
  );
}
